import { useEffect, useState } from 'react';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACGroup } from '../../../types';
import { isOnPremGroup } from '../../acls/isOnPremGroup';
import { useQueryErrorToast } from '../../graph/hooks/useQueryErrorToast';
import {
  useGetACLGroupsQuery,
  useGetRoleACLGroupMembershipQuery,
} from '../../graph/services/graphApi';
import GroupAutoComplete from '../../users/components/GroupAutoComplete2';
import GroupDisplayItem from '../../users/components/GroupDisplayItem';
import {
  selectAclAdds,
  selectAclDeletes,
  stageAdd,
  stageDelete,
} from '../services/roleManagementSlice';

import StagedCountBadges from './StagedCountBadges';

type Props = {
  role: RBACGroup;
};

// The ACL groups this role is nested into — i.e. the access it confers. Direct
// nestings are removable; any reached via ACL-in-ACL nesting show with the
// non-removable "Transitive" badge (managed at their source), exactly like the
// Users page. This single panel is the effective-access view (see CONTEXT.md).
export default function RoleACLPanel({ role }: Props) {
  const dispatch = useAppDispatch();
  const { data: acls, isError } = useGetRoleACLGroupMembershipQuery(role.id);
  useQueryErrorToast(isError, 'Failed to load ACL nesting');
  const { data: allAclGroups } = useGetACLGroupsQuery();

  const adds = useAppSelector(selectAclAdds);
  const deletes = useAppSelector(selectAclDeletes);

  // On-prem-mastered ACLs can't be nested into from the cloud (Graph rejects the write),
  // so they're hidden from the picker unless this is toggled on — and even then,
  // selecting one warns instead of staging. Mirrors the ACLs page "Show on-prem groups".
  const [showOnPrem, setShowOnPrem] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  // Drop a stale "can't be nested" warning when the selected role changes — the panel
  // stays mounted across role switches, so the warning would otherwise linger. Mirrors
  // the ACLs page clearing its role warning on aclId change.
  useEffect(() => {
    setWarning(null);
  }, [role.id]);

  // Exclude ACLs the role already confers (direct or transitive) and already-staged
  // adds, so the autocomplete only offers ACLs that can actually be nested — matching
  // the Members / Owners panels. On-prem-mastered ACLs are excluded unless revealed.
  const excludedIds = new Set([...(acls ?? []), ...adds].map((item) => item.id));
  const aclCandidates = (allAclGroups ?? []).filter(
    (group) =>
      group.displayName &&
      group.id &&
      !excludedIds.has(group.id) &&
      (showOnPrem || !isOnPremGroup(group))
  );

  // Block nesting into an on-prem ACL (only reachable when the toggle reveals them):
  // the membership lives in on-prem AD, so warn rather than stage a write that fails.
  const handleAddAcl = (acl: RBACGroup) => {
    if (isOnPremGroup(acl)) {
      setWarning(`${acl.displayName} is managed in on-prem AD and can't be nested here.`);
      return;
    }
    setWarning(null);
    dispatch(stageAdd({ bucket: 'aclNesting', item: acl }));
  };

  const addedIds = new Set(adds.map((item) => item.id));
  const deletedIds = new Set(deletes.map((item) => item.id));
  const rows = [...adds, ...(acls ?? [])];

  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col h-full min-h-0">
      <div className="flex flex-row items-center justify-between gap-2 pb-2">
        <div className="flex flex-row items-center gap-2 min-w-0">
          <div className="font-bold truncate">Associated ACL Memberships</div>
          <div className="shrink-0">
            <StagedCountBadges addCount={adds.length} deleteCount={deletes.length} />
          </div>
        </div>
        <div className="w-56 shrink-0">
          <GroupAutoComplete
            items={aclCandidates}
            onSelect={handleAddAcl}
            enabled
            clearOnSelect
            placeholder="Add this role to an ACL"
          />
          {/* Mirrors the ACLs page toggle: switch on the left, label text to its right. */}
          <label className="cursor-pointer label justify-start gap-2">
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={showOnPrem}
              onChange={() => setShowOnPrem(!showOnPrem)}
            />
            <span className="text-xs">Show on-prem ACLs</span>
          </label>
          {warning && <div className="text-xs text-warning">{warning}</div>}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm opacity-60">This role confers no ACLs.</div>
      ) : (
        <div className="border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
          <ul className="divide-y divide-base-200 h-full overflow-y-auto">
            {rows.map((acl) => {
              const transitive = 'transitive' in acl ? acl.transitive : false;
              const added = addedIds.has(acl.id);
              const deleted = deletedIds.has(acl.id);
              return (
                <GroupDisplayItem
                  key={added || deleted ? acl.id + 'pending' : acl.id}
                  displayName={acl.displayName!}
                  roleId={acl.id!}
                  selectable={false}
                  transitive={transitive}
                  onPrem={isOnPremGroup(acl)}
                  added={added}
                  deleted={deleted}
                  onDelete={() => dispatch(stageDelete({ bucket: 'aclNesting', item: acl }))}
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
